//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { EditorState } from 'draft-js';
import { createContext, useContext } from 'react';

import {
  IGetShowroomShortLinkRequest,
  IGetShowroomShortLinkResponse,
} from '@apis/showroom/showroomApiModel';
import { Props as EmojiPickerProps } from '@components/atoms/emoji-picker/EmojiPicker';
import {
  DropdownSection,
  DropdownValue,
  IFacebookPageSuggestion,
  ILinkShorteningModalConfiguration,
  IMentionsSuggestionsQuery,
  IRecommendedHashtag,
  MentionsSuggestion,
  ShowroomLinkCallback,
} from '@components/molecules/smart-input/smartInputModel';
import FacebookPageId from '@models/FacebookPageId';
import { SocialTextParseMethod } from '@utils/social-text-utils/socialTextUtilsModel';

export interface ISmartInputContext {
  editor: {
    focusEditor: () => void;
    getEditorState: () => EditorState;
    isFocused: boolean;
    resetEditorState: (value: string) => void;
    setEditorState: (
      newEditorState: EditorState,
      shouldFocus?: boolean,
    ) => void;
    setReadOnly: (shouldReadOnly: boolean) => void;
  };
  emojiPicker?: {
    isEnabled: boolean;
    placement: EmojiPickerProps['placement'];
  };
  generativeAiCaption?: {
    isEnabled: boolean;
    isEnabledForPlanTier: boolean;
    isModalTriggerEnabled: boolean;
    getGeneratedCaption: () => Promise<string | undefined>;
    openModal: () => void;
    disabledTierTooltipContent?: React.ReactNode;
  };
  generativeAiResponse?: {
    isAiGenerativeResponseLoading: boolean;
    isEnabled: boolean;
    shouldDisableMenu: boolean;
    dropdownConfig: DropdownSection | undefined;
    getGeneratedResponse: (value: DropdownValue) => Promise<string>;
  };
  linkShortening?: {
    isEnabled: boolean;
    getOriginalLink: (shortLink: string) => Promise<string | undefined>;
    getShortLink: (originalLink: string) => Promise<string | undefined>;
    openModal: (configuration: ILinkShorteningModalConfiguration) => void;
    additionalInfo?: React.ReactElement;
    isRevenueTrackingEnabled?: boolean;
  };
  showroomLinks?: {
    isEnabled: boolean;
    getShowroomLink: (showroomLinkCallback: ShowroomLinkCallback) => void;
    getShowroomShortlink: (
      req: IGetShowroomShortLinkRequest,
    ) => Promise<IGetShowroomShortLinkResponse>;
  };
  mentions?: {
    getFacebookPageById?: (
      pageId: FacebookPageId,
    ) => Promise<IFacebookPageSuggestion | undefined>;
    getMentionsSuggestions?: (
      query: string,
    ) => Promise<MentionsSuggestion[] | undefined>;
    isDropdownOpen: boolean;
    isEnabled: boolean;
    isInstagramUserEmbedEnabled: boolean;
    setIsDropdownOpen: (
      shouldOpen: boolean,
      shouldFocusEditor?: boolean,
    ) => void;
    setMentionsSuggestionsQuery: (query: IMentionsSuggestionsQuery) => void;
  };
  recommendedHashtags?: {
    isEnabled: boolean;
    isDropdownOpen: boolean;
    recommendations: IRecommendedHashtag[];
    setIsDropdownOpen: (
      shouldOpen: boolean,
      shouldFocusEditor?: boolean,
    ) => void;
  };
  socialTextParseMethod: SocialTextParseMethod;
  trackingIdPrefix: string;
}

/**
 * Default context to store the details of the current SmartInput,
 */
const SmartInputContext = createContext<ISmartInputContext>({
  editor: {
    focusEditor: () => undefined,
    getEditorState: () => EditorState.createEmpty(),
    isFocused: false,
    resetEditorState: (value) => {
      void value;
    },
    setEditorState: (newEditorState) => {
      void newEditorState;
    },
    setReadOnly: (shouldReadOnly) => {
      void shouldReadOnly;
    },
  },
  socialTextParseMethod: (_text) => [],
  trackingIdPrefix: '',
});

export const SmartInputContextProvider = SmartInputContext.Provider;

/**
 * Get the current SmartInput context.
 */
export const useSmartInputContext = () => {
  return useContext(SmartInputContext);
};
